/**
 * Appointment-type adapter for the generic ResourceCatalog (GDN-2335).
 *
 * Normalizes the promotion dry-run row shape (FixtureType — the same seam
 * the cleanup wizard reads) into resource-agnostic CatalogRows plus the
 * display-ready TypeDefaults the expandable detail renders. The catalog
 * itself never learns appointment-type vocabulary; it all arrives here.
 */
import type { TypeDefaults } from '@components/ui/cards/TypeDefaultsPreview';
import { mapAppointmentTypeToDefaults } from '@components/ui/cards/TypeDefaultsPreview';
import type {
  CatalogLabels,
  CatalogRow,
} from '@components/ui/catalog/ResourceCatalog/ResourceCatalog.types';
// Type-only import via the direct /types path — never the feature barrel
// (it auto-registers the feature at module load).
import type { FixtureType } from '@features/practice-cleanup/types';

// Direct file import (not the hooks barrel) so story files that transitively
// load this adapter never trigger feature auto-registration side effects.
import { getColorSwatchFromEnum } from '../hooks/useColorFromEnum';

/** One appointment type, catalog-ready: the row plus its detail payload. */
export interface AppointmentTypeCatalogEntry {
  row: CatalogRow;
  defaults: TypeDefaults;
}

const toLinkedScope = (
  templateScope: FixtureType['template_scope']
): CatalogRow['linkedScope'] => {
  if (templateScope === 'org') return 'shared';
  if (templateScope === 'user') return 'personal';
  // 'public' passes through; null stays null.
  return templateScope;
};

export const toCatalogEntry = (t: FixtureType): AppointmentTypeCatalogEntry => {
  const scope: CatalogRow['scope'] =
    t.owner_scope === 'organization' ? 'shared' : 'personal';

  return {
    row: {
      id: t.encounter_type_id,
      name: t.encounter_type,
      // Backend sends enum tokens ('color1'…'color10'); the catalog renders
      // CatalogRow.color directly, so resolve to a real swatch value here.
      color: getColorSwatchFromEnum(t.color),
      scope,
      ownerLabel: scope === 'personal' ? t.provider : null,
      usage: t.usage,
      lastUsedAt: t.last_used_at ?? null,
      linkedLabel: t.template_title,
      linkedScope: toLinkedScope(t.template_scope),
      archived: Boolean(t.archived),
    },
    // The form/reminder rules live nested under default_action_rules on the
    // row; the mapper wants them flat (same bridge as the wizard's toVersion).
    defaults: mapAppointmentTypeToDefaults(
      {
        template_id: t.template_id,
        default_duration: t.default_duration,
        default_modality: t.default_modality,
        default_place_of_service: t.default_place_of_service,
        default_cpt_codes: t.default_cpt_codes,
        default_form_rules: t.default_action_rules?.form_rules ?? null,
        default_reminder_rules: t.default_action_rules?.reminder_rules ?? null,
      },
      { templateName: t.template_title }
    ),
  };
};

export const toCatalogEntries = (
  types: FixtureType[]
): AppointmentTypeCatalogEntry[] => types.map(toCatalogEntry);

/** The catalog's display vocabulary for the appointment-type resource. */
export const APPOINTMENT_TYPE_CATALOG_LABELS: CatalogLabels = {
  noun: 'appointment type',
  nounPlural: 'appointment types',
  sharedScopeLabel: 'Practice-wide',
  personalScopeLabel: 'Personal',
  publicScopeLabel: 'Public',
  linkedColumnHeader: 'Template',
  ownerFilterLabel: 'Clinician',
  expandDetailTooltip: 'Show visit defaults',
  collapseDetailTooltip: 'Hide visit defaults',
  // Matches the cleanup wizard's education copy — same reassurance, same facts.
  archiveExplanation:
    'Archived appointment types stay on past notes and appointments — they ' +
    'just stop showing up when scheduling. You can restore them anytime.',
};
